import React, { useContext, useEffect, useState } from 'react';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import useFetch from 'use-http';
import DialogList from 'src/dialogs';
import { DocumentTypes } from 'src/models/DocumentTypes';
import moment from 'moment';
import DocumentManagement from 'src/components/layout/DocumentManagement';
import ConfirmDeleteDocDialog from '../ConfirmDeleteDocDialog';

type fileDataType = {
    id: number;
    originalFileName: string;
    fileSize: number;
    fileExtension: string;
    fileType: string;
    s3FileId: number;
    s3KeyName: string;
    uploadDate: Date;
    note: string;
    docType: string;
};

// TODO: Replace contractHoldId with contract lookup Id for all types
interface IContractholdDocsProps {
    showDialog: (dialogName: string) => void;
    hideDialog: () => void;

    contractLookupId?: number;
    contractType?: string;
}
const ContractHoldDocs = (props: IContractholdDocsProps) => {
    const appContext = useContext<IAppContextState>(AppContext);
    const [fileData, setFileData] = useState(undefined);
    const [fileIdToDelete, setFileIdToDelete] = useState(undefined);
    const [context, setContext] = useState('');
    const { get, request, response } = useFetch();

    useEffect(() => {
        switch (props.contractType) {
            case 'Leasehold':
                setContext('lease');
                break;
            case 'Contract Hold':
                setContext('contract hold');
                break;
            default:
                break;
        }
    }, [props]);

    const loadData = async () => {
        // TODO: Remove default path once leaseholds are consolidated.
        let filePath = ``;

        switch (props.contractType) {
            // TODO: Contract Type is consolidated now. Prop shouldn't be necessary anymore.
            case 'Leasehold':
                break;
            case 'Contract Hold':
                filePath = `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/contract-holds/${props.contractLookupId}/s3bucket`;
                break;    
            default:
                break;
            }
            
        const data = await get(filePath);
        if (response.ok) {
            setFileData(data);
        }
    };

    useEffect(() => {
        if (props.contractLookupId > 0) {
            loadData();
        }
    }, [props.contractLookupId]);

    const downloadFile = async (keyName: string, index: number, fileName: string) => {
        await get(`/v1.0/s3bucket/${keyName}`);
        if (response.ok) {
            await response.blob().then((blob) => {
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.getElementById(`a_${index}`) as HTMLAnchorElement;
                a.href = blobUrl;
                a.download = fileName;
                a.click();
                window.URL.revokeObjectURL(blobUrl);
            });
        }
    };

    const deleteFile = async (s3FileId: number) => {
        if (!isNaN(s3FileId)) {
            setFileIdToDelete(s3FileId);
            props.showDialog(DialogList.DELETE_DOC);
        }
    };

    const deleteFileConfirmed = async () => {
        props.hideDialog();
        await request.delete(`/v1.0/s3bucket/${fileIdToDelete}`);
        setFileIdToDelete(undefined);
        loadData();
    };

    const deleteFileCanceled = () => {
        props.hideDialog();
        setFileIdToDelete(undefined);
    };

    const docType = (docTypeValue: string) => {
        const numDocType = parseInt(docTypeValue, 10);
        if (numDocType) {
            const type = DocumentTypes.find((dc) => dc.id === numDocType) || { name: 'Bad Doc Type' };
            return type;
        }
        return { name: 'No Doc Type Set' };
    };

    const displayFiles = () => {
        if (fileData) {
            return fileData.map((file: fileDataType, index: number) => {
                return (
                    <div
                        key={index}
                        style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid darkgrey' }}
                        className="mb-2"
                    >
                        <div style={{ width: '10%', textAlign: 'center' }}>{moment(file.uploadDate).format('MM-DD-YYYY')}</div>
                        <div style={{ width: '30%', textAlign: 'center' }}>{file.originalFileName}</div>
                        <div style={{ width: '10%', textAlign: 'center' }}>{file.fileSize / 1000 || 'NA'}Kb</div>
                        <div style={{ width: '10%', textAlign: 'center' }}>{docType(file.docType).name}</div>
                        <div style={{ width: '25%', textAlign: 'center' }}>{file.note}</div>
                        <div style={{ width: '15%', textAlign: 'center' }}>
                            <a onClick={() => downloadFile(file.s3KeyName, index, file.originalFileName)}>
                                <i style={{ fontSize: '1.7rem' }} className="far fa-download mr-1 clickable" />
                            </a>

                            <a onClick={() => deleteFile(file.id)} style={{ color: 'red' }}>
                                <i style={{ fontSize: '1.7rem' }} className="far fa-trash mr-1 clickable" />
                            </a>
                        </div>
                        <a id={`a_${index}`} />
                    </div>
                );
            });
        } else {
            return <div>No Files Found</div>;
        }
    };

    const filesUploaded = () => {
        props.hideDialog();
        loadData();
    };

    return (
        <>
            <div>
                <div style={{ display: 'flex' }}>
                    <div className="mr-2" style={{ width: '70%' }}>
                        <div style={{ fontSize: '1.75rem' }}>Attached Files</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div style={{ width: '10%', textAlign: 'center', fontSize: '1.6rem' }}>Upload Date</div>
                            <div style={{ width: '30%', textAlign: 'center', fontSize: '1.6rem' }}>Filename</div>
                            <div style={{ width: '10%', textAlign: 'center', fontSize: '1.6rem' }}>File Size</div>
                            <div style={{ width: '10%', textAlign: 'center', fontSize: '1.6rem' }}>Doc Type</div>
                            <div style={{ width: '25%', textAlign: 'center', fontSize: '1.6rem' }}>Note</div>
                            <div style={{ width: '15%', textAlign: 'center', fontSize: '1.6rem' }}>Actions</div>
                        </div>

                        <div style={{ maxHeight: '60rem', overflow: 'auto' }}>{displayFiles()}</div>
                    </div>

                    <div style={{ borderLeft: '2px solid darkgrey' }} className="mr-2" />

                    <div>
                        <div>
                            <div style={{ fontSize: '1.75rem' }}>Generate Documents</div>
                            {/* {generateMOR()} */}
                            <div>No Reports Currently Available For Leasehold</div>
                        </div>
                    </div>
                </div>
                <Button
                    className="blue-hover-button"
                    style={{marginRight: '1rem'}}
                    text="Attach Document"
                    size="medium"
                    intent="primary"
                    onClick={() => props.showDialog(DialogList.DOCUMENT_MANAGEMENT)}
                    disabled={props.contractLookupId > 0 ? false : true}
                />
            </div>
            <DocumentManagement filesUploaded={filesUploaded} context={context} contractLookupId={props.contractLookupId} />
            <ConfirmDeleteDocDialog confirmDelete={deleteFileConfirmed} cancelDelete={deleteFileCanceled} />
        </>
    );
};

export default ContractHoldDocs;



// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/redesign/ContractHoldDocs.tsx